/**
 * One leg on the manifest — a traveller, their flight, and whether it is real.
 *
 * Wade, 2026-08-17: *"Manifest should show focus flights and booking status
 * (clearly realized booked)."*
 *
 * "Clearly" is the whole requirement. Before this the state was a 10px lowercase
 * word in muted grey — technically present, invisible in practice. Now booked
 * state is carried FOUR ways at once, so it survives a glance, a greyscale
 * print-out and a screen reader:
 *
 *   1. a 4px accent rail down the left of the card (emerald / amber);
 *   2. a filled pill with a check or a clock — not an outline, not grey;
 *   3. the WORD, in caps, inside the pill;
 *   4. `aria-label` on the row saying it in a sentence.
 *
 * ⚠️ Never colour alone: about 8% of men cannot separate the emerald from the
 * amber, and the print-out is often photocopied. Glyph + word do the work; colour
 * only makes it fast.
 *
 * ⚠️ And the rule that outranks the styling: a BOOKED leg prints the booking
 * record's flight or NOTHING. A focus flight is what we are shopping — shown for
 * an UNBOOKED leg and labelled "focus", never promoted into a ticket.
 */
import { Check, Clock, PlaneLanding, PlaneTakeoff, Plus } from 'lucide-react';
import type { ManifestRow } from '@/pages/eventManifestView';
import { legDate, legFlightLabel, personLabel, shiftDate } from '@/pages/eventManifestView';
import type { ItineraryItemType } from '@/lib/types';

/** Initials bubble, matching the itinerary's companion bubbles. */
function Initials({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))]/[0.12] text-[10px] font-bold text-[hsl(var(--accent-foreground))]"
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
}

/** Sep 14 from 2026-09-14, without dragging in a date library. */
export function shortDate(ymd: string | null): string | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd || null;
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export interface ManifestLegRowProps {
  row: ManifestRow;
  /** Open the add dialog for this traveller on this date. Absent = read-only. */
  onAdd?: (memberId: string, dayDate: string, itemType: ItineraryItemType) => void;
}

export default function ManifestLegRow({ row, onAdd }: ManifestLegRowProps) {
  const { person, leg } = row;
  const isReturn = leg.leg_role === 'return';
  const Icon = isReturn ? PlaneTakeoff : PlaneLanding;
  const date = legDate(leg);
  const flight = legFlightLabel(leg);

  // "+ before" / "+ after", in the traveller's own terms. An outbound is framed
  // around LANDING (add something after you land); a return around LEAVING.
  const beforeDate = isReturn ? date : shiftDate(date, -1);
  const afterDate = isReturn ? shiftDate(date, 1) : date;
  const canAdd = !!onAdd && !!person.member_id && !!person.trip_id;

  const stateLabel = leg.booked
    ? flight
      ? `booked on ${flight.text}`
      : 'booked, flight not recorded'
    : 'not booked';

  return (
    <div
      className={
        'break-inside-avoid rounded-lg border border-l-4 bg-card px-3 py-2.5 shadow-paper ' +
        (leg.booked
          ? 'border-border border-l-emerald-500'
          : 'border-border border-l-amber-400 bg-amber-50/[0.35]')
      }
      aria-label={`${person.display_name}, ${leg.origin} to ${leg.destination}, ${stateLabel}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Initials name={person.display_name} />

        <span className="text-sm font-semibold text-foreground">{personLabel(person)}</span>
        {person.home_airport && (
          <span className="text-xs text-muted-foreground">from {person.home_airport}</span>
        )}

        {/* Booked state, unmissable. Filled, glyphed and worded. */}
        <span
          className={
            'ml-auto inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ' +
            (leg.booked
              ? 'bg-emerald-600 text-white'
              : 'bg-amber-500 text-white')
          }
        >
          {leg.booked ? (
            <Check className="h-3 w-3" aria-hidden="true" />
          ) : (
            <Clock className="h-3 w-3" aria-hidden="true" />
          )}
          {leg.booked ? 'Booked' : 'Not booked'}
        </span>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 pl-10 text-sm">
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="font-mono font-semibold">
          {leg.origin}
          <span className="mx-1 text-muted-foreground">→</span>
          {leg.destination}
        </span>
        {date && <span className="text-muted-foreground">{shortDate(date)}</span>}

        {/* A ticket, or a target — never conflated. */}
        {flight && (
          <span className="inline-flex items-center gap-1">
            <span className="font-mono text-xs font-semibold">{flight.text}</span>
            {flight.isFocus && (
              <span className="rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/[0.06] px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-[hsl(var(--accent-foreground))]">
                focus
              </span>
            )}
          </span>
        )}
        {leg.booked && !leg.flight && (
          <span className="text-xs italic text-muted-foreground">flight not recorded</span>
        )}

        {leg.when && <span className="text-xs text-muted-foreground">{leg.when}</span>}
        {leg.booking_ref && (
          <span className="font-mono text-xs text-muted-foreground">ref {leg.booking_ref}</span>
        )}
        {leg.fare !== null && (
          <span className="ml-auto font-mono text-sm font-semibold">
            ${leg.fare}
            <span className="ml-0.5 text-[10px] font-normal text-muted-foreground">
              {leg.price_basis === 'total' ? 'total' : 'pp'}
            </span>
          </span>
        )}
      </div>

      {/* The one place the times can lie: ticketed on one flight, timed from
          another. Say so rather than letting an organizer trust it. */}
      {leg.focus_matches_booking === false && (
        <p className="mt-1.5 pl-10 text-xs text-amber-700">
          Ticketed on {leg.flight}; the time above is the flight we were shopping
          {leg.focus_flight ? ` (${leg.focus_flight})` : ''} — confirm with the airline.
        </p>
      )}

      {canAdd && (
        <div className="no-print mt-2 flex flex-wrap items-center gap-1.5 pl-10">
          {beforeDate && (
            <button
              type="button"
              onClick={() => onAdd!(person.member_id!, beforeDate, 'activity')}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/50 hover:bg-[hsl(var(--primary))]/[0.06] hover:text-[hsl(var(--accent-foreground))]"
              aria-label={`Add something for ${person.display_name} before this ${isReturn ? 'return' : 'flight'}`}
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              before
            </button>
          )}
          {afterDate && (
            <button
              type="button"
              onClick={() => onAdd!(person.member_id!, afterDate, 'activity')}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/50 hover:bg-[hsl(var(--primary))]/[0.06] hover:text-[hsl(var(--accent-foreground))]"
              aria-label={`Add something for ${person.display_name} after this ${isReturn ? 'return' : 'landing'}`}
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              {isReturn ? 'after' : 'after landing'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
